import { useHaptics } from '@/hooks/useHaptics';
import { BOUNCE, useSlidingIndicator } from '@/hooks/useSlidingIndicator';
import { cn } from '@/lib/cn';

/**
 * Schede con sottolineatura: una riga di base attraversa tutta la larghezza, e
 * la voce attiva è marcata da un tratto più spesso in accento.
 *
 * **Quando usare questo e non `Segmented`.** I due fanno la stessa cosa e non
 * sono intercambiabili a piacere:
 *
 * - `Segmented` è una superficie: pesa, e per questo si vede da lontano. Giusto
 *   per il selettore PRINCIPALE di una schermata.
 * - `UnderlineTabs` è quasi solo testo. Giusto per un selettore SECONDARIO —
 *   in particolare quando sta sotto un segmentato: due contenitori pieni
 *   incolonnati si somigliano troppo e non si capisce più quale comanda cosa.
 *
 * È anche la forma che regge molte voci: la fila scorre e l'unico segno è sotto
 * l'attiva, mentre con le pastiglie ogni voce porta una superficie e la riga
 * diventa un muro.
 */
export function UnderlineTabs<T extends string>({
  options,
  value,
  onChange,
  className,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  className?: string;
}) {
  const haptics = useHaptics();
  const { containerRef, rect, ready } = useSlidingIndicator(value);

  return (
    // La riga di base sta sul contenitore ESTERNO, che non scorre: sullo
    // scroller sarebbe lunga quanto le schede e finirebbe a metà schermo
    // quando sono poche, oppure scorrerebbe via insieme a loro.
    <div className={cn('divider-edge border-b', className)}>
      <div className="no-scrollbar overflow-x-auto">
        <div ref={containerRef} className="relative flex gap-1" role="tablist">
          {/* Il tratto attivo è UNO che scorre da una scheda all'altra, non un
              bordo che si accende e si spegne: così si vede il percorso, e su
              una fila lunga si capisce in che direzione ci si è spostati. */}
          {rect && (
            <span
              aria-hidden
              className="absolute bottom-0 h-0.5 rounded-full bg-brand"
              style={{
                left: rect.left,
                width: rect.width,
                transition: ready ? `left 320ms ${BOUNCE}, width 320ms ${BOUNCE}` : undefined,
              }}
            />
          )}
          {options.map((o) => {
            const on = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="tab"
                aria-selected={on}
                data-active={on}
                onClick={() => {
                  if (on) return;
                  haptics.selection();
                  onChange(o.value);
                }}
                className={cn(
                  'relative whitespace-nowrap px-3 pb-2 pt-1.5 text-[13px] font-semibold transition-colors',
                  on ? 'text-brand' : 'text-fg-muted',
                )}
              >
                {o.label}
              </button>
            );
          })}

          {/* Spaziatore di coda: il padding finale di un flex in overflow viene
              mangiato, e l'ultima scheda finirebbe incollata al bordo. */}
          <span aria-hidden className="w-3 shrink-0" />
        </div>
      </div>
    </div>
  );
}
